import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  MediaGenerationSettingsResponse,
  PresenterProfileResponse,
} from "pages/media/types";
import { loadImageRatio } from "../helpers";

/**
 * Image used to derive the avatar's aspect ratio — the full extracted/custom
 * asset (not the thumbnail), matching the image rendered on the canvas for a
 * Medium shot.
 */
export const getPresenterImagePath = (
  presenter: PresenterProfileResponse | undefined
): string | undefined =>
  presenter?.face_profile?.extracted_asset_path ??
  presenter?.face_profile?.custom_asset_path ??
  undefined;

/**
 * Loads the image aspect ratio (keyed by presenter id) of every presenter
 * actually used by the project: the project-level presenter plus each chapter's
 * per-chapter override. Only these are fetched — not the whole org — so every
 * chapter's avatar can be sized/positioned against its own image.
 */
export const usePresenterRatios = ({
  projectPresenter,
  projectPresenters,
  chapters,
}: {
  projectPresenter: PresenterProfileResponse | undefined;
  projectPresenters: PresenterProfileResponse[] | undefined;
  chapters: MediaGenerationSettingsResponse["chapters"] | undefined;
}): { ratiosById: Map<string, number>; isLoading: boolean } => {
  const entries = useMemo(() => {
    const presentersById = new Map(
      (projectPresenters ?? []).map((presenter) => [presenter.id, presenter])
    );
    // id -> image path, deduped (same id overwrites) and skipping presenters
    // without a face image.
    const addPath = (
      pathById: Map<string, string>,
      presenter: PresenterProfileResponse | undefined
    ) => {
      const path = getPresenterImagePath(presenter);
      if (presenter?.id && path) pathById.set(presenter.id, path);
      return pathById;
    };
    const pathById = (chapters ?? []).reduce(
      (acc, chapter) =>
        chapter.presenter_id
          ? addPath(acc, presentersById.get(chapter.presenter_id))
          : acc,
      addPath(new Map<string, string>(), projectPresenter)
    );
    return [...pathById.entries()].map(([id, path]) => ({ id, path }));
  }, [projectPresenter, projectPresenters, chapters]);

  const results = useQueries({
    queries: entries.map(({ path }) => ({
      queryKey: ["imageRatio", "presenter", path],
      queryFn: () => loadImageRatio(path),
      staleTime: Infinity,
    })),
  });

  const ratiosById = useMemo(() => {
    const ratiosById = new Map<string, number>();
    entries.forEach(({ id }, index) => {
      const ratio = results[index]?.data;
      if (typeof ratio === "number") ratiosById.set(id, ratio);
    });
    return ratiosById;
  }, [entries, results]);

  const isLoading = results.some((result) => result.isLoading);

  return { ratiosById, isLoading };
};
